import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Save, Type, Eye, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'sonner';
import { GiftCardEmailSettingsService, GiftCardEmailSettings, defaultGiftCardEmailSettings } from '../services/giftCardEmailSettingsService';
import { generateGiftCardEmailHTML } from '../services/giftCardEmailService';
import { generateGiftCardImage } from '../services/giftCardImageService';
import { GiftCardSettings, defaultGiftCardSettings } from '../services/giftCardSettingsService';
import { supabase } from '../lib/supabase';

interface GiftCardEmailSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GiftCardEmailSettingsModal({ isOpen, onClose }: GiftCardEmailSettingsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'checkout' | 'header' | 'content' | 'instructions' | 'footer' | 'preview'>('checkout');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewImageDataUrl, setPreviewImageDataUrl] = useState<string | undefined>();
  const [stripeStatus, setStripeStatus] = useState<{ isActive: boolean; accountId: string | null } | null>(null);
  
  const [settings, setSettings] = useState<GiftCardEmailSettings>({
    ...defaultGiftCardEmailSettings,
    business_id: selectedBusiness?.id || ''
  });

  const [previewDesign, setPreviewDesign] = useState<GiftCardSettings | null>(null);

  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadSettings();
      loadPreviewDesign();
      checkStripeStatus();
    }
  }, [isOpen, selectedBusiness?.id]);

  const checkStripeStatus = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data: businessData, error } = await supabase
        .from('businesses')
        .select('stripe_connect_account_id, stripe_connect_charges_enabled')
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error checking Stripe status:', error);
        setStripeStatus({ isActive: false, accountId: null });
        return;
      }

      const isActive = !!(businessData?.stripe_connect_account_id && businessData?.stripe_connect_charges_enabled);
      setStripeStatus({
        isActive,
        accountId: businessData?.stripe_connect_account_id || null
      });
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      setStripeStatus({ isActive: false, accountId: null });
    }
  };

  useEffect(() => {
    if (activeTab === 'preview' && previewDesign && selectedBusiness?.id) {
      generatePreviewImage();
    }
  }, [activeTab, previewDesign, selectedBusiness?.id]);

  const loadSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const emailSettings = await GiftCardEmailSettingsService.getEmailSettings(selectedBusiness.id);
      if (emailSettings) {
        setSettings(emailSettings);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const loadPreviewDesign = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Load the first available gift card design, or use defaults
      const { data: designs } = await supabase
        .from('gift_card_settings')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .limit(1);

      if (designs && designs.length > 0) {
        setPreviewDesign(designs[0] as GiftCardSettings);
      } else {
        // Use default design if none exists
        setPreviewDesign({
          ...defaultGiftCardSettings,
          business_id: selectedBusiness.id
        } as GiftCardSettings);
      }
    } catch (error) {
      console.error('Error loading preview design:', error);
      // Use defaults on error
      setPreviewDesign({
        ...defaultGiftCardSettings,
        business_id: selectedBusiness.id
      } as GiftCardSettings);
    }
  };

  const generatePreviewImage = async () => {
    if (!previewDesign || !selectedBusiness?.id) return;

    try {
      setPreviewLoading(true);
      const imageDataUrl = await generateGiftCardImage({
        giftCardCode: 'GIFT-XXXX-XXXX-XXXX',
        design: previewDesign,
        businessLogoUrl: selectedBusiness.booking_settings?.logo_url,
        recipientName: 'Sample Recipient',
        width: 600
      });
      setPreviewImageDataUrl(imageDataUrl);
    } catch (error) {
      console.error('Error generating preview image:', error);
      setPreviewImageDataUrl(undefined);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setSaving(true);
      const success = await GiftCardEmailSettingsService.saveEmailSettings(
        selectedBusiness.id,
        settings
      );

      if (success) {
        toast.success('Email settings saved successfully');
        onClose();
      } else {
        toast.error('Failed to save email settings');
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof GiftCardEmailSettings, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Generate preview email HTML - must be before early return
  const previewEmailHTML = useMemo(() => {
    if (!previewDesign || !selectedBusiness || !isOpen) return '';

    return generateGiftCardEmailHTML({
      giftCardCode: 'GIFT-XXXX-XXXX-XXXX',
      amount: 100.00,
      design: previewDesign,
      businessName: selectedBusiness.name || 'Your Business',
      businessLogoUrl: selectedBusiness.booking_settings?.logo_url,
      recipientName: 'Sample Recipient',
      isForRecipient: true,
      giftCardImageDataUrl: previewImageDataUrl,
      emailSettings: settings
    });
  }, [settings, previewDesign, selectedBusiness, previewImageDataUrl, isOpen]);

  const tabs = [
    { id: 'checkout' as const, label: 'Checkout Settings', icon: CreditCard },
    { id: 'header' as const, label: 'Header & Greeting', icon: Type },
    { id: 'content' as const, label: 'Content', icon: Mail },
    { id: 'instructions' as const, label: 'Instructions', icon: Type },
    { id: 'footer' as const, label: 'Footer', icon: Type },
    { id: 'preview' as const, label: 'Preview', icon: Eye },
  ];

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[10000]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[800px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'env(safe-area-inset-bottom, 0px)', height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Gift Card Email Settings
                </h2>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-gray-100 border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-3 border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Checkout Settings Tab */}
                {activeTab === 'checkout' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Stripe Integration Status</h3>
                      {stripeStatus === null ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : stripeStatus.isActive ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                          <div className="flex items-start space-x-3">
                            <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-lg font-semibold text-green-900 mb-1">
                                Stripe Checkout Active
                              </h4>
                              <p className="text-sm text-green-700">
                                Your Stripe integration is active and customers can purchase gift cards through the public gift card page.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-lg font-semibold text-yellow-900 mb-1">
                                Checkout Disabled
                              </h4>
                              <p className="text-sm text-yellow-700 mb-3">
                                Please activate Stripe integration to enable gift card purchases on your public gift card page.
                              </p>
                              <p className="text-xs text-yellow-600">
                                Go to Settings → Integrations to connect your Stripe account.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Header & Greeting Tab */}
                {activeTab === 'header' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Header</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Header Text (for Recipient)
                          </label>
                          <input
                            type="text"
                            value={settings.email_header_recipient}
                            onChange={(e) => updateSetting('email_header_recipient', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="You've Received a Gift Card!"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Shown when gift card is sent to a recipient
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Header Text (for Purchaser)
                          </label>
                          <input
                            type="text"
                            value={settings.email_header_purchaser}
                            onChange={(e) => updateSetting('email_header_purchaser', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Your Gift Card is Ready!"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Shown when gift card is sent to the purchaser
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Greeting</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Greeting (for Recipient)
                          </label>
                          <input
                            type="text"
                            value={settings.email_greeting_recipient}
                            onChange={(e) => updateSetting('email_greeting_recipient', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Hi {recipient_name},"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{recipient_name}'} as a placeholder for the recipient's name
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Greeting (for Purchaser)
                          </label>
                          <input
                            type="text"
                            value={settings.email_greeting_purchaser}
                            onChange={(e) => updateSetting('email_greeting_purchaser', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Hello,"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Main Message</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message (for Recipient)
                          </label>
                          <textarea
                            rows={3}
                            value={settings.email_message_recipient}
                            onChange={(e) => updateSetting('email_message_recipient', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="You've received a gift card from {business_name}!"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{business_name}'} as a placeholder for your business name
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Message (for Purchaser)
                          </label>
                          <textarea
                            rows={3}
                            value={settings.email_message_purchaser}
                            onChange={(e) => updateSetting('email_message_purchaser', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="Your gift card from {business_name} has been created successfully."
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{business_name}'} as a placeholder for your business name
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Details Section</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Details Section Title
                          </label>
                          <input
                            type="text"
                            value={settings.email_details_title}
                            onChange={(e) => updateSetting('email_details_title', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Gift Card Details"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Gift Card Code Label
                          </label>
                          <input
                            type="text"
                            value={settings.email_code_label}
                            onChange={(e) => updateSetting('email_code_label', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Gift Card Code:"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount Label
                          </label>
                          <input
                            type="text"
                            value={settings.email_amount_label}
                            onChange={(e) => updateSetting('email_amount_label', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Amount:"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructions Tab */}
                {activeTab === 'instructions' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions Section</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instructions Title
                          </label>
                          <input
                            type="text"
                            value={settings.email_instructions_title}
                            onChange={(e) => updateSetting('email_instructions_title', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="How to Use Your Gift Card:"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instruction 1
                          </label>
                          <textarea
                            rows={2}
                            value={settings.email_instruction_1}
                            onChange={(e) => updateSetting('email_instruction_1', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="Present this gift card code when making a purchase: {gift_card_code}"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{gift_card_code}'} as a placeholder for the gift card code
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instruction 2
                          </label>
                          <textarea
                            rows={2}
                            value={settings.email_instruction_2}
                            onChange={(e) => updateSetting('email_instruction_2', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="The gift card has a balance of {amount}"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{amount}'} as a placeholder for the gift card amount
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instruction 3
                          </label>
                          <textarea
                            rows={2}
                            value={settings.email_instruction_3}
                            onChange={(e) => updateSetting('email_instruction_3', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="You can use it for any service or product at {business_name}"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{business_name}'} as a placeholder for your business name
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instruction 4
                          </label>
                          <textarea
                            rows={2}
                            value={settings.email_instruction_4}
                            onChange={(e) => updateSetting('email_instruction_4', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="Save this email for your records"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer Tab */}
                {activeTab === 'footer' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Footer Text</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Footer Main Text
                          </label>
                          <input
                            type="text"
                            value={settings.email_footer_text}
                            onChange={(e) => updateSetting('email_footer_text', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Thank you for choosing {business_name}!"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Use {'{business_name}'} as a placeholder for your business name
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Footer Subtext
                          </label>
                          <input
                            type="text"
                            value={settings.email_footer_subtext}
                            onChange={(e) => updateSetting('email_footer_subtext', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="If you have any questions, please contact us."
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Typography</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Font Style
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            {(['sans-serif', 'serif', 'monospace', 'cursive'] as const).map((font) => (
                              <button
                                key={font}
                                type="button"
                                onClick={() => updateSetting('font_family', font)}
                                className={`px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                  settings.font_family === font
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                                style={{
                                  fontFamily: font === 'sans-serif' 
                                    ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                    : font === 'serif'
                                    ? 'Georgia, "Times New Roman", serif'
                                    : font === 'monospace'
                                    ? '"Courier New", Courier, monospace'
                                    : '"Comic Sans MS", "Brush Script MT", cursive'
                                }}
                              >
                                {font === 'sans-serif' ? 'Sans Serif' : 
                                 font === 'serif' ? 'Serif' :
                                 font === 'monospace' ? 'Monospace' : 'Cursive'}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Choose the font style for your email text
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview Tab */}
                {activeTab === 'preview' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Preview</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        This is how your gift card email will look to recipients. The preview uses sample data.
                      </p>
                      
                      {previewLoading ? (
                        <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-sm text-gray-600">Generating preview...</p>
                          </div>
                        </div>
                      ) : previewEmailHTML ? (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                            <p className="text-xs text-gray-600 font-medium">
                              Email Preview - Sample Recipient
                            </p>
                          </div>
                          <div className="overflow-auto max-h-[600px] p-4">
                            <iframe
                              srcDoc={previewEmailHTML}
                              className="w-full border-0"
                              style={{ minHeight: '600px' }}
                              title="Email Preview"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600">Unable to generate preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

